const DEBUG_TOOLS_LOCAL_STORAGE_KEY = "DRIVENOW_DEBUG_TOOLS"

type ConsoleMethod = "log" | "info" | "warn" | "error" | "debug"

type BufferedConsoleEntry = {
  method: ConsoleMethod
  args: unknown[]
  timestamp: number
}

const consoleMethods: ConsoleMethod[] = ["log", "info", "warn", "error", "debug"]

function applyDebugToolsQueryParamGate(): void {
  const params = new URLSearchParams(window.location.search)
  const value = params.get("debugtools") ?? params.get("devtools")
  if (value == null) return

  try {
    if (["0", "false", "off", "no"].includes(value.toLowerCase())) {
      window.localStorage.removeItem(DEBUG_TOOLS_LOCAL_STORAGE_KEY)
      console.info("[debug-tools] disabled via debugtools/devtools query param")
    } else {
      window.localStorage.setItem(DEBUG_TOOLS_LOCAL_STORAGE_KEY, "1")
      console.info("[debug-tools] enabled via debugtools/devtools query param")
    }
  } catch (e) {
    console.warn("[debug-tools] failed to update localStorage gate", e)
  }
}

function debugToolsEnabled(): boolean {
  try {
    return window.localStorage.getItem(DEBUG_TOOLS_LOCAL_STORAGE_KEY) !== null
  } catch (_) {
    return false
  }
}

function installConsoleBuffer() {
  const buffer: BufferedConsoleEntry[] = []
  const originals = Object.fromEntries(consoleMethods.map((method) => [method, console[method]])) as Record<ConsoleMethod, typeof console.log>

  for (const method of consoleMethods) {
    console[method] = (...args: unknown[]) => {
      buffer.push({ method, args, timestamp: performance.now() })
      originals[method](...args)
    }
  }

  return {
    buffer,
    restore() {
      for (const method of consoleMethods) {
        console[method] = originals[method]
      }
    },
  }
}

function installLongTaskObserver(): void {
  if (!("PerformanceObserver" in window)) {
    console.warn("[debug-tools] PerformanceObserver is not available")
    return
  }

  const supportedEntryTypes = PerformanceObserver.supportedEntryTypes || []
  if (!supportedEntryTypes.includes("longtask")) {
    console.warn("[debug-tools] PerformanceObserver longtask entries are not supported")
    return
  }

  try {
    new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        console.warn("[longtask]", {
          duration: Math.round(entry.duration),
          startTime: Math.round(entry.startTime),
          name: entry.name,
          entryType: entry.entryType,
        })
      }
    }).observe({ entryTypes: ["longtask"] })
  } catch (e) {
    console.warn("[debug-tools] failed to install longtask observer", e)
  }
}

/**
 * Enables optional in-page mobile debugging tools when localStorage contains
 * DRIVENOW_DEBUG_TOOLS. Set with:
 *
 *   localStorage.setItem("DRIVENOW_DEBUG_TOOLS", "1")
 *
 * Or from a mobile browser URL:
 *
 *   ?debugtools=1   enable and persist
 *   ?debugtools=0   disable and persist
 *   ?devtools=1     enable and persist
 *   ?devtools=0     disable and persist
 *
 * The Eruda bundle is lazy loaded so normal users do not download it. Console
 * output between this installer running and Eruda loading is buffered and replayed
 * into Eruda so early page/turbo logs are still visible in the in-page console.
 */
export function initDebugToolsFromLocalStorageGate(): void {
  applyDebugToolsQueryParamGate()
  if (!debugToolsEnabled()) return

  const consoleBuffer = installConsoleBuffer()
  installLongTaskObserver()

  import("eruda")
    .then(({ default: eruda }) => {
      consoleBuffer.restore()
      eruda.init({
        tool: ["console", "network", "resources", "info"],
        defaults: {
          displaySize: 50,
        },
      })

      const erudaConsole = eruda.get("console")
      erudaConsole.info("[debug-tools] enabled via localStorage", {
        key: DEBUG_TOOLS_LOCAL_STORAGE_KEY,
        url: window.location.href,
      })
      for (const entry of consoleBuffer.buffer) {
        erudaConsole[entry.method](
          `[buffered +${Math.round(entry.timestamp)}ms]`,
          ...entry.args,
        )
      }
    })
    .catch((e) => {
      consoleBuffer.restore()
      console.warn("[debug-tools] failed to load eruda", e)
    })
}
